/**
 * API client Textprint — Sprint 2 (Devis + Lignes + Sous-traitants).
 * Brief SIM-PLATFORM-TEXTIPRINT-2026-001 §C + §H.
 *
 * Endpoints : /v1/operations/textprint/sprint2/{devis,sous-traitants}
 */
import { apiClient, unwrap, unwrapPaginated } from './client';
import type { Paginated } from './operations';
import type {
  DevisImpression, LigneDevisImpression, SousTraitant,
  StatutDevisImpression, StatutSousTraitant, SpecialiteSousTraitant,
  CommandeImpression,
} from '@/types';

// ── Pagination commune ────────────────────────────────────────────────────
interface BasePaging {
  page?:   number;
  limit?:  number;
  search?: string;
}

// ══════════════════════════════════════════════════════════════════════════
// DEVIS
// ══════════════════════════════════════════════════════════════════════════

export interface DevisListParams extends BasePaging {
  statut?:    StatutDevisImpression;
  clientId?:  string;
}

export interface CreateLigneDevisInlinePayload {
  ordre?:            number;
  designation:       string;
  famille?:          string;
  quantite:          number;
  unite?:            string;
  prixUnitaireCents: number;
  remisePct?:        number;
  notes?:            string;
}

export interface CreateDevisPayload {
  reference?:               string;
  clientId?:                string;
  dateDevis?:               string;
  validiteJours?:           number;
  remisePct?:               number;
  acompteDemandeCents?:     number;
  acomptePct?:              number;
  statut?:                  StatutDevisImpression;
  delaiLivraisonJours?:     number;
  conditionsParticulieres?: string;
  notes?:                   string;
  lignes?:                  CreateLigneDevisInlinePayload[];
}
export type UpdateDevisPayload = Partial<Omit<CreateDevisPayload, 'reference' | 'lignes'>>;

export interface ConvertirDevisPayload {
  dateLivraisonPrevue?: string;
  adresseLivraison?:    string;
  instructions?:        string;
}

export interface ConvertirDevisResponse {
  devis:    DevisImpression;
  commande: CommandeImpression;
}

export const textprintDevisApi = {
  list: async (params: DevisListParams = {}): Promise<Paginated<DevisImpression>> => {
    const r = await apiClient.get('/operations/textprint/sprint2/devis', { params });
    return unwrapPaginated<DevisImpression>(r);
  },
  get: async (id: string): Promise<DevisImpression> => {
    const r = await apiClient.get(`/operations/textprint/sprint2/devis/${id}`);
    return unwrap<DevisImpression>(r);
  },
  create: async (payload: CreateDevisPayload): Promise<DevisImpression> => {
    const r = await apiClient.post('/operations/textprint/sprint2/devis', payload);
    return unwrap<DevisImpression>(r);
  },
  update: async (id: string, payload: UpdateDevisPayload): Promise<DevisImpression> => {
    const r = await apiClient.patch(`/operations/textprint/sprint2/devis/${id}`, payload);
    return unwrap<DevisImpression>(r);
  },
  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`/operations/textprint/sprint2/devis/${id}`);
  },
  envoyer: async (id: string): Promise<DevisImpression> => {
    const r = await apiClient.post(`/operations/textprint/sprint2/devis/${id}/envoyer`);
    return unwrap<DevisImpression>(r);
  },
  accepter: async (id: string): Promise<DevisImpression> => {
    const r = await apiClient.post(`/operations/textprint/sprint2/devis/${id}/accepter`);
    return unwrap<DevisImpression>(r);
  },
  refuser: async (id: string): Promise<DevisImpression> => {
    const r = await apiClient.post(`/operations/textprint/sprint2/devis/${id}/refuser`);
    return unwrap<DevisImpression>(r);
  },
  convertir: async (id: string, payload: ConvertirDevisPayload = {}): Promise<ConvertirDevisResponse> => {
    const r = await apiClient.post(`/operations/textprint/sprint2/devis/${id}/convertir`, payload);
    return unwrap<ConvertirDevisResponse>(r);
  },
};

// ══════════════════════════════════════════════════════════════════════════
// LIGNES DEVIS
// ══════════════════════════════════════════════════════════════════════════

export interface CreateLigneDevisPayload {
  ordre?:            number;
  designation:       string;
  famille?:          string;
  quantite:          number;
  unite?:            string;
  prixUnitaireCents: number;
  remisePct?:        number;
  notes?:            string;
}
export type UpdateLigneDevisPayload = Partial<CreateLigneDevisPayload>;

export const textprintLignesDevisApi = {
  list: async (devisId: string): Promise<LigneDevisImpression[]> => {
    const r = await apiClient.get(`/operations/textprint/sprint2/devis/${devisId}/lignes`);
    return unwrap<LigneDevisImpression[]>(r);
  },
  create: async (devisId: string, payload: CreateLigneDevisPayload): Promise<LigneDevisImpression> => {
    const r = await apiClient.post(`/operations/textprint/sprint2/devis/${devisId}/lignes`, payload);
    return unwrap<LigneDevisImpression>(r);
  },
  update: async (devisId: string, lineId: string, payload: UpdateLigneDevisPayload): Promise<LigneDevisImpression> => {
    const r = await apiClient.patch(`/operations/textprint/sprint2/devis/${devisId}/lignes/${lineId}`, payload);
    return unwrap<LigneDevisImpression>(r);
  },
  remove: async (devisId: string, lineId: string): Promise<void> => {
    await apiClient.delete(`/operations/textprint/sprint2/devis/${devisId}/lignes/${lineId}`);
  },
};

// ══════════════════════════════════════════════════════════════════════════
// SOUS-TRAITANTS
// ══════════════════════════════════════════════════════════════════════════

export interface SousTraitantListParams extends BasePaging {
  statut?:     StatutSousTraitant;
  specialite?: SpecialiteSousTraitant;
}

export interface CreateSousTraitantPayload {
  raisonSociale:    string;
  specialite?:      SpecialiteSousTraitant;
  specialiteAutre?: string;
  contact?:         string;
  phone?:           string;
  email?:           string;
  adresse?:         string;
  delaiMoyenJours?: number;
  qualiteNote?:     number;
  tarifsHabituels?: Record<string, unknown>;
  notes?:           string;
  statut?:          StatutSousTraitant;
}
export type UpdateSousTraitantPayload = Partial<CreateSousTraitantPayload>;

export const textprintSousTraitantsApi = {
  list: async (params: SousTraitantListParams = {}): Promise<Paginated<SousTraitant>> => {
    const r = await apiClient.get('/operations/textprint/sprint2/sous-traitants', { params });
    return unwrapPaginated<SousTraitant>(r);
  },
  get: async (id: string): Promise<SousTraitant> => {
    const r = await apiClient.get(`/operations/textprint/sprint2/sous-traitants/${id}`);
    return unwrap<SousTraitant>(r);
  },
  create: async (payload: CreateSousTraitantPayload): Promise<SousTraitant> => {
    const r = await apiClient.post('/operations/textprint/sprint2/sous-traitants', payload);
    return unwrap<SousTraitant>(r);
  },
  update: async (id: string, payload: UpdateSousTraitantPayload): Promise<SousTraitant> => {
    const r = await apiClient.patch(`/operations/textprint/sprint2/sous-traitants/${id}`, payload);
    return unwrap<SousTraitant>(r);
  },
  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`/operations/textprint/sprint2/sous-traitants/${id}`);
  },
};
